import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  createPartVendor,
  deletePartVendor,
  deletePartVendorAttachment,
  downloadPartVendorAttachment,
  fetchPartVendor,
  fetchPartVendors,
  fetchPartVendorTradeHistory,
  updatePartVendor,
  uploadPartVendorAttachment,
} from '../../api/partVendor';
import { useCompanyStore } from '../../store/companyStore';
import {notifyError, reportError} from '../../utils/notify';
import { acceptanceStatusLabel } from '../../types/acceptanceInspection';
import {
  formatFileSize,
  formatPartVendorAddress,
  PartVendor,
  PartVendorAttachment,
  PartVendorAttachmentKind,
  PartVendorPayload,
  PartVendorTradeHistory,
  PartVendorTradeStatus,
  partVendorAttachmentKindLabel,
  partVendorTradeStatusLabel,
} from '../../types/partVendor';

import '../contract/ContractPanel.css';
import './VendorPanel.css';
import VendorAssessmentSection from './VendorAssessmentSection';

type StatusFilter = 'ALL' | PartVendorTradeStatus;
type ViewMode = 'list' | 'create' | 'edit' | 'detail';
type DetailTab = 'info' | 'trade' | 'files' | 'assess';

type PendingFile = {
  file: File;
  kind: PartVendorAttachmentKind;
};

const EMPTY_FORM: PartVendorPayload = {
  vendorName: '',
  businessNumber: '',
  ceoName: '',
  businessType: '',
  businessItem: '',
  contactName: '',
  contactPosition: '',
  phone: '',
  mobile: '',
  fax: '',
  email: '',
  zipCode: '',
  address: '',
  addressDetail: '',
  supplyItems: '',
  paymentTerms: '',
  bankName: '',
  accountNumber: '',
  accountHolder: '',
  tradeStatus: 'ACTIVE',
  memo: '',
};

function textOrDash(value?: string | null) {
  const v = value?.trim();
  return v ? v : '-';
}

function VendorPanel() {
  const companyId = useCompanyStore((state) => state.companyId);
  const [rows, setRows] = useState<PartVendor[]>([]);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [detailTab, setDetailTab] = useState<DetailTab>('info');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<PartVendor | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<PartVendorPayload>({ ...EMPTY_FORM });
  const [savedAttachments, setSavedAttachments] = useState<PartVendorAttachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploadKind, setUploadKind] = useState<PartVendorAttachmentKind>('BUSINESS_LICENSE');
  const [uploading, setUploading] = useState(false);
  const [tradeRows, setTradeRows] = useState<PartVendorTradeHistory[]>([]);
  const [tradeLoading, setTradeLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await fetchPartVendors(keyword));
      setError('');
    } catch {
      reportError(setError, '업체 목록을 불러오지 못했습니다.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [keyword]);

  useEffect(() => {
    setViewMode('list');
    const timer = window.setTimeout(() => void load(), 200);
    return () => window.clearTimeout(timer);
  }, [companyId, load, setViewMode]);

  const filteredRows = useMemo(() => {
    if (statusFilter === 'ALL') return rows;
    return rows.filter((row) => (row.tradeStatus || 'ACTIVE') === statusFilter);
  }, [rows, statusFilter]);

  const patchForm = <K extends keyof PartVendorPayload>(key: K, value: PartVendorPayload[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const fillFromRow = (row: PartVendor) => {
    setForm({
      vendorName: row.vendorName || '',
      businessNumber: row.businessNumber || '',
      ceoName: row.ceoName || '',
      businessType: row.businessType || '',
      businessItem: row.businessItem || '',
      contactName: row.contactName || '',
      contactPosition: row.contactPosition || '',
      phone: row.phone || '',
      mobile: row.mobile || '',
      fax: row.fax || '',
      email: row.email || '',
      zipCode: row.zipCode || '',
      address: row.address || '',
      addressDetail: row.addressDetail || '',
      supplyItems: row.supplyItems || '',
      paymentTerms: row.paymentTerms || '',
      bankName: row.bankName || '',
      accountNumber: row.accountNumber || '',
      accountHolder: row.accountHolder || '',
      tradeStatus: (row.tradeStatus as PartVendorTradeStatus) || 'ACTIVE',
      memo: row.memo || '',
    });
    setSavedAttachments(row.attachments ?? []);
    setPendingFiles([]);
  };

  const backToList = () => {
    setViewMode('list');
    setEditingId(null);
    setDetail(null);
    setDetailTab('info');
    setForm({ ...EMPTY_FORM });
    setSavedAttachments([]);
    setPendingFiles([]);
    setUploadKind('BUSINESS_LICENSE');
    setTradeRows([]);
  };

  const openCreate = () => {
    setSelectedId(null);
    setEditingId(null);
    setDetail(null);
    setForm({ ...EMPTY_FORM });
    setSavedAttachments([]);
    setPendingFiles([]);
    setUploadKind('BUSINESS_LICENSE');
    setError('');
    setViewMode('create');
  };

  const openEdit = async (row: PartVendor) => {
    setSelectedId(row.vendorId);
    setEditingId(row.vendorId);
    setDetail(row);
    fillFromRow(row);
    setError('');
    setViewMode('edit');
    try {
      const next = await fetchPartVendor(row.vendorId);
      setDetail(next);
      fillFromRow(next);
    } catch {
      // 목록 데이터로 유지
    }
  };

  const openDetail = async (row: PartVendor) => {
    setSelectedId(row.vendorId);
    setEditingId(row.vendorId);
    setDetail(row);
    setSavedAttachments(row.attachments ?? []);
    setDetailTab('info');
    setError('');
    setViewMode('detail');
    setTradeLoading(true);
    try {
      const [next, history] = await Promise.all([
        fetchPartVendor(row.vendorId),
        fetchPartVendorTradeHistory(row.vendorId),
      ]);
      setDetail(next);
      setSavedAttachments(next.attachments ?? []);
      setTradeRows(history);
    } catch {
      reportError(setError, '업체 상세를 불러오지 못했습니다.');
      setTradeRows([]);
    } finally {
      setTradeLoading(false);
    }
  };

  const loadTradeHistory = async (vendorId: number) => {
    try {
      setTradeLoading(true);
      setTradeRows(await fetchPartVendorTradeHistory(vendorId));
    } catch {
      setTradeRows([]);
    } finally {
      setTradeLoading(false);
    }
  };

  const selectRow = (row: PartVendor) => {
    setSelectedId(row.vendorId);
  };

  const uploadPending = async (vendorId: number, files: PendingFile[]) => {
    for (const item of files) {
      await uploadPartVendorAttachment(vendorId, item.file, item.kind);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.vendorName.trim()) {
      notifyError('업체명을 입력해 주세요.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload: PartVendorPayload = {
        vendorName: form.vendorName.trim(),
        businessNumber: form.businessNumber?.trim() || undefined,
        ceoName: form.ceoName?.trim() || undefined,
        businessType: form.businessType?.trim() || undefined,
        businessItem: form.businessItem?.trim() || undefined,
        contactName: form.contactName?.trim() || undefined,
        contactPosition: form.contactPosition?.trim() || undefined,
        phone: form.phone?.trim() || undefined,
        mobile: form.mobile?.trim() || undefined,
        fax: form.fax?.trim() || undefined,
        email: form.email?.trim() || undefined,
        zipCode: form.zipCode?.trim() || undefined,
        address: form.address?.trim() || undefined,
        addressDetail: form.addressDetail?.trim() || undefined,
        supplyItems: form.supplyItems?.trim() || undefined,
        paymentTerms: form.paymentTerms?.trim() || undefined,
        bankName: form.bankName?.trim() || undefined,
        accountNumber: form.accountNumber?.trim() || undefined,
        accountHolder: form.accountHolder?.trim() || undefined,
        tradeStatus: form.tradeStatus || 'ACTIVE',
        memo: form.memo?.trim() || undefined,
      };
      let vendorId = editingId;
      if (editingId) {
        await updatePartVendor(editingId, {
          ...payload,
          businessNumber: payload.businessNumber ?? '',
          ceoName: payload.ceoName ?? '',
          businessType: payload.businessType ?? '',
          businessItem: payload.businessItem ?? '',
          contactName: payload.contactName ?? '',
          contactPosition: payload.contactPosition ?? '',
          phone: payload.phone ?? '',
          mobile: payload.mobile ?? '',
          fax: payload.fax ?? '',
          email: payload.email ?? '',
          zipCode: payload.zipCode ?? '',
          address: payload.address ?? '',
          addressDetail: payload.addressDetail ?? '',
          supplyItems: payload.supplyItems ?? '',
          paymentTerms: payload.paymentTerms ?? '',
          bankName: payload.bankName ?? '',
          accountNumber: payload.accountNumber ?? '',
          accountHolder: payload.accountHolder ?? '',
          memo: payload.memo ?? '',
        });
        if (pendingFiles.length > 0) {
          await uploadPending(editingId, pendingFiles);
        }
      } else {
        const created = await createPartVendor(payload);
        vendorId = created.vendorId;
        if (pendingFiles.length > 0) {
          await uploadPending(created.vendorId, pendingFiles);
        }
      }
      await load();
      if (vendorId) {
        try {
          const next = await fetchPartVendor(vendorId);
          await openDetail(next);
          return;
        } catch {
          // fall through
        }
      }
      backToList();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '업체 저장에 실패했습니다.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: PartVendor) => {
    if (!window.confirm(`「${row.vendorName}」 업체를 삭제할까요?`)) return;
    try {
      await deletePartVendor(row.vendorId);
      if (selectedId === row.vendorId || editingId === row.vendorId) {
        backToList();
        setSelectedId(null);
      }
      await load();
    } catch {
      notifyError('업체 삭제에 실패했습니다.');
    }
  };

  const handlePickFiles = (fileList: FileList | null) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;
    setPendingFiles((prev) => [...prev, ...files.map((file) => ({ file, kind: uploadKind }))]);
  };

  const handleUploadNow = async (fileList: FileList | null) => {
    if (!editingId || viewMode === 'create') {
      handlePickFiles(fileList);
      return;
    }
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;
    try {
      setUploading(true);
      setError('');
      let latest: PartVendor | null = null;
      for (const file of files) {
        latest = await uploadPartVendorAttachment(editingId, file, uploadKind);
      }
      if (latest) {
        setDetail(latest);
        setSavedAttachments(latest.attachments ?? []);
      }
      await load();
    } catch (err) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '첨부파일 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (attachment: PartVendorAttachment) => {
    if (!editingId) return;
    try {
      const blob = await downloadPartVendorAttachment(editingId, attachment.attachmentId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      notifyError('첨부파일 다운로드에 실패했습니다.');
    }
  };

  const handleDeleteAttachment = async (attachment: PartVendorAttachment) => {
    if (!editingId) return;
    if (!window.confirm(`「${attachment.fileName}」 파일을 삭제할까요?`)) return;
    try {
      await deletePartVendorAttachment(editingId, attachment.attachmentId);
      setSavedAttachments((prev) =>
        prev.filter((item) => item.attachmentId !== attachment.attachmentId)
      );
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              attachments: (prev.attachments ?? []).filter(
                (item) => item.attachmentId !== attachment.attachmentId
              ),
              attachmentCount: Math.max(0, (prev.attachmentCount ?? 1) - 1),
            }
          : prev
      );
      await load();
    } catch {
      notifyError('첨부파일 삭제에 실패했습니다.');
    }
  };

  const renderAttachmentEditor = (immediateUpload: boolean) => (
    <div className="vendor-form__files">
      <div className="vendor-form__file-toolbar">
        <select
          value={uploadKind}
          onChange={(e) => setUploadKind(e.target.value as PartVendorAttachmentKind)}
        >
          <option value="BUSINESS_LICENSE">사업자등록증</option>
          <option value="OTHER">기타 서류</option>
        </select>
        <label className="is-upload">
          {uploading ? '업로드 중...' : immediateUpload ? '파일 업로드' : '파일 추가'}
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.hwp,.zip"
            multiple
            disabled={uploading || saving}
            onChange={(e) => {
              if (immediateUpload) {
                void handleUploadNow(e.target.files);
              } else {
                handlePickFiles(e.target.files);
              }
              e.target.value = '';
            }}
          />
        </label>
        <p className="vendor-form__file-hint">
          {immediateUpload
            ? '선택 즉시 업로드됩니다. PDF·이미지 권장.'
            : '저장 시 함께 업로드됩니다. PDF·이미지 권장.'}
        </p>
      </div>

      {savedAttachments.length > 0 ? (
        <ul className="vendor-form__file-list">
          {savedAttachments.map((file) => (
            <li key={file.attachmentId} className="vendor-form__file-item">
              <div className="vendor-form__file-meta">
                <strong>{file.fileName}</strong>
                <span>
                  {partVendorAttachmentKindLabel(file.attachmentKind)}
                  {file.fileSize != null ? ` · ${formatFileSize(file.fileSize)}` : ''}
                </span>
              </div>
              <div className="vendor-form__file-actions">
                <button type="button" className="is-ghost" onClick={() => void handleDownload(file)}>
                  다운로드
                </button>
                <button
                  type="button"
                  className="is-danger"
                  onClick={() => void handleDeleteAttachment(file)}
                >
                  삭제
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {pendingFiles.length > 0 ? (
        <ul className="vendor-form__file-list">
          {pendingFiles.map((item, index) => (
            <li key={`${item.file.name}-${index}`} className="vendor-form__file-item">
              <div className="vendor-form__file-meta">
                <strong>{item.file.name}</strong>
                <span>
                  {partVendorAttachmentKindLabel(item.kind)} · 저장 시 업로드
                  {item.file.size ? ` · ${formatFileSize(item.file.size)}` : ''}
                </span>
              </div>
              <div className="vendor-form__file-actions">
                <button
                  type="button"
                  className="is-danger"
                  onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                >
                  제거
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {savedAttachments.length === 0 && pendingFiles.length === 0 ? (
        <p className="vendor-form__file-hint">첨부된 파일이 없습니다.</p>
      ) : null}
    </div>
  );

  const renderForm = () => (
    <form className="vendor-form" onSubmit={(e) => void handleSubmit(e)}>
      <div className="vendor-form__header">
        <div>
          <h3>{viewMode === 'edit' ? '업체 수정' : '업체 등록'}</h3>
          <p>필수 항목(*)만 채우면 저장할 수 있고, 나머지는 나중에 보완할 수 있습니다.</p>
        </div>
      </div>

      <div className="vendor-form__body">
        <section className="vendor-form__section">
          <div className="vendor-form__section-head">
            <strong>기본 정보</strong>
            <span>사업자·대표·업태</span>
          </div>
          <div className="vendor-form__grid">
            <label className="vendor-form__field is-span-2">
              <span>
                업체명<em>*</em>
              </span>
              <input
                value={form.vendorName}
                onChange={(e) => patchForm('vendorName', e.target.value)}
                placeholder="예: 한빛전자 주식회사"
                required
              />
            </label>
            <label className="vendor-form__field">
              <span>사업자등록번호</span>
              <input
                value={form.businessNumber}
                onChange={(e) => patchForm('businessNumber', e.target.value)}
                placeholder="000-00-00000"
              />
            </label>
            <label className="vendor-form__field">
              <span>거래상태</span>
              <select
                value={form.tradeStatus || 'ACTIVE'}
                onChange={(e) => patchForm('tradeStatus', e.target.value as PartVendorTradeStatus)}
              >
                <option value="ACTIVE">거래중</option>
                <option value="SUSPENDED">거래중단</option>
              </select>
            </label>
            <label className="vendor-form__field">
              <span>대표자</span>
              <input
                value={form.ceoName}
                onChange={(e) => patchForm('ceoName', e.target.value)}
                placeholder="대표자명"
              />
            </label>
            <label className="vendor-form__field">
              <span>업태</span>
              <input
                value={form.businessType}
                onChange={(e) => patchForm('businessType', e.target.value)}
                placeholder="예: 제조업"
              />
            </label>
            <label className="vendor-form__field is-span-2">
              <span>종목</span>
              <input
                value={form.businessItem}
                onChange={(e) => patchForm('businessItem', e.target.value)}
                placeholder="예: 전자부품 도매"
              />
            </label>
            <label className="vendor-form__field is-span-4">
              <span>주력 납품 품목</span>
              <input
                value={form.supplyItems}
                onChange={(e) => patchForm('supplyItems', e.target.value)}
                placeholder="예: LED 모듈, PCB, 커넥터"
              />
            </label>
          </div>
        </section>

        <section className="vendor-form__section">
          <div className="vendor-form__section-head">
            <strong>담당자 · 연락처</strong>
            <span>인수검사 공급업체 자동입력에 사용</span>
          </div>
          <div className="vendor-form__grid">
            <label className="vendor-form__field">
              <span>담당자</span>
              <input
                value={form.contactName}
                onChange={(e) => patchForm('contactName', e.target.value)}
                placeholder="담당자명"
              />
            </label>
            <label className="vendor-form__field">
              <span>직책</span>
              <input
                value={form.contactPosition}
                onChange={(e) => patchForm('contactPosition', e.target.value)}
                placeholder="예: 영업팀장"
              />
            </label>
            <label className="vendor-form__field">
              <span>대표전화</span>
              <input
                value={form.phone}
                onChange={(e) => patchForm('phone', e.target.value)}
                placeholder="02-0000-0000"
              />
            </label>
            <label className="vendor-form__field">
              <span>휴대폰</span>
              <input
                value={form.mobile}
                onChange={(e) => patchForm('mobile', e.target.value)}
                placeholder="010-0000-0000"
              />
            </label>
            <label className="vendor-form__field">
              <span>팩스</span>
              <input
                value={form.fax}
                onChange={(e) => patchForm('fax', e.target.value)}
                placeholder="02-0000-0001"
              />
            </label>
            <label className="vendor-form__field is-span-2">
              <span>이메일</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => patchForm('email', e.target.value)}
                placeholder="sales@example.com"
              />
            </label>
          </div>
        </section>

        <section className="vendor-form__section">
          <div className="vendor-form__section-head">
            <strong>사업장 주소</strong>
            <span>납품·방문 기준 주소</span>
          </div>
          <div className="vendor-form__grid">
            <label className="vendor-form__field">
              <span>우편번호</span>
              <input
                value={form.zipCode}
                onChange={(e) => patchForm('zipCode', e.target.value)}
                placeholder="00000"
              />
            </label>
            <label className="vendor-form__field is-span-3">
              <span>주소</span>
              <input
                value={form.address}
                onChange={(e) => patchForm('address', e.target.value)}
                placeholder="시/군/구 도로명 주소"
              />
            </label>
            <label className="vendor-form__field is-span-4">
              <span>상세주소</span>
              <input
                value={form.addressDetail}
                onChange={(e) => patchForm('addressDetail', e.target.value)}
                placeholder="건물명 · 호수 등"
              />
            </label>
          </div>
        </section>

        <section className="vendor-form__section">
          <div className="vendor-form__section-head">
            <strong>결제 · 계좌</strong>
            <span>대금 지급 참고 정보</span>
          </div>
          <div className="vendor-form__grid">
            <label className="vendor-form__field is-span-2">
              <span>결제조건</span>
              <input
                value={form.paymentTerms}
                onChange={(e) => patchForm('paymentTerms', e.target.value)}
                placeholder="예: 월말 마감 익월 15일 지급"
              />
            </label>
            <label className="vendor-form__field">
              <span>은행</span>
              <input
                value={form.bankName}
                onChange={(e) => patchForm('bankName', e.target.value)}
                placeholder="예: 국민은행"
              />
            </label>
            <label className="vendor-form__field">
              <span>예금주</span>
              <input
                value={form.accountHolder}
                onChange={(e) => patchForm('accountHolder', e.target.value)}
                placeholder="예금주명"
              />
            </label>
            <label className="vendor-form__field is-span-2">
              <span>계좌번호</span>
              <input
                value={form.accountNumber}
                onChange={(e) => patchForm('accountNumber', e.target.value)}
                placeholder="000-00-0000-000"
              />
            </label>
          </div>
        </section>

        <section className="vendor-form__section">
          <div className="vendor-form__section-head">
            <strong>첨부서류</strong>
            <span>사업자등록증 · 기타 서류</span>
          </div>
          {renderAttachmentEditor(viewMode === 'edit')}
        </section>

        <section className="vendor-form__section">
          <div className="vendor-form__section-head">
            <strong>메모</strong>
            <span>품질·납기·특이사항</span>
          </div>
          <div className="vendor-form__grid">
            <label className="vendor-form__field is-span-4">
              <span>비고</span>
              <textarea
                value={form.memo}
                onChange={(e) => patchForm('memo', e.target.value)}
                placeholder="납품 리드타임, 품질 이슈, 방문 가능 요일 등"
              />
            </label>
          </div>
        </section>

        <div className="vendor-form__actions">
          <button type="button" className="is-ghost" onClick={backToList}>
            취소
          </button>
          <button type="submit" className="is-primary" disabled={saving || uploading}>
            {saving ? '저장 중...' : viewMode === 'edit' ? '수정 저장' : '등록'}
          </button>
        </div>
      </div>
    </form>
  );

  const renderDetail = () => {
    if (!detail) return null;
    const suspended = detail.tradeStatus === 'SUSPENDED';
    return (
      <div className="vendor-detail">
        <div className="vendor-detail__hero">
          <div>
            <h3>{detail.vendorName}</h3>
            <p>
              {textOrDash(detail.businessNumber)} · {textOrDash(detail.ceoName)} ·{' '}
              {partVendorTradeStatusLabel(detail.tradeStatus)}
            </p>
          </div>
          <div className="vendor-detail__actions">
            <button type="button" className="is-primary" onClick={() => void openEdit(detail)}>
              수정
            </button>
            <button type="button" className="is-danger" onClick={() => void handleDelete(detail)}>
              삭제
            </button>
          </div>
        </div>

        <div className="vendor-panel__tabs" style={{ borderBottom: '1px solid #eef1f5' }}>
          {(
            [
              { key: 'info', label: '기본정보' },
              { key: 'trade', label: `거래내역${tradeRows.length ? ` (${tradeRows.length})` : ''}` },
              {
                key: 'files',
                label: `첨부서류${
                  (detail.attachmentCount ?? savedAttachments.length)
                    ? ` (${detail.attachmentCount ?? savedAttachments.length})`
                    : ''
                }`,
              },
              { key: 'assess', label: '조사/평가' },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={detailTab === tab.key ? 'is-active' : undefined}
              onClick={() => {
                setDetailTab(tab.key);
                if (tab.key === 'trade' && editingId) {
                  void loadTradeHistory(editingId);
                }
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="vendor-detail__body">
          {detailTab === 'info' ? (
            <>
              <div className="vendor-detail__section">
                <h4>기본 정보</h4>
                <dl className="vendor-detail__grid">
                  <div className="vendor-detail__item">
                    <dt>거래상태</dt>
                    <dd>
                      <span
                        className={`vendor-panel__badge ${
                          suspended ? 'is-suspended' : 'is-active'
                        }`}
                      >
                        {partVendorTradeStatusLabel(detail.tradeStatus)}
                      </span>
                    </dd>
                  </div>
                  <div className="vendor-detail__item">
                    <dt>사업자등록번호</dt>
                    <dd className={detail.businessNumber ? undefined : 'is-muted'}>
                      {textOrDash(detail.businessNumber)}
                    </dd>
                  </div>
                  <div className="vendor-detail__item">
                    <dt>대표자</dt>
                    <dd className={detail.ceoName ? undefined : 'is-muted'}>
                      {textOrDash(detail.ceoName)}
                    </dd>
                  </div>
                  <div className="vendor-detail__item">
                    <dt>업태</dt>
                    <dd className={detail.businessType ? undefined : 'is-muted'}>
                      {textOrDash(detail.businessType)}
                    </dd>
                  </div>
                  <div className="vendor-detail__item is-span-2">
                    <dt>종목</dt>
                    <dd className={detail.businessItem ? undefined : 'is-muted'}>
                      {textOrDash(detail.businessItem)}
                    </dd>
                  </div>
                  <div className="vendor-detail__item is-span-2">
                    <dt>주력 납품 품목</dt>
                    <dd className={detail.supplyItems ? undefined : 'is-muted'}>
                      {textOrDash(detail.supplyItems)}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="vendor-detail__section">
                <h4>담당자 · 연락처</h4>
                <dl className="vendor-detail__grid">
                  <div className="vendor-detail__item">
                    <dt>담당자</dt>
                    <dd>{textOrDash(detail.contactName)}</dd>
                  </div>
                  <div className="vendor-detail__item">
                    <dt>직책</dt>
                    <dd>{textOrDash(detail.contactPosition)}</dd>
                  </div>
                  <div className="vendor-detail__item">
                    <dt>대표전화</dt>
                    <dd>{textOrDash(detail.phone)}</dd>
                  </div>
                  <div className="vendor-detail__item">
                    <dt>휴대폰</dt>
                    <dd>{textOrDash(detail.mobile)}</dd>
                  </div>
                  <div className="vendor-detail__item">
                    <dt>팩스</dt>
                    <dd>{textOrDash(detail.fax)}</dd>
                  </div>
                  <div className="vendor-detail__item is-span-2">
                    <dt>이메일</dt>
                    <dd>{textOrDash(detail.email)}</dd>
                  </div>
                </dl>
              </div>

              <div className="vendor-detail__section">
                <h4>사업장 주소</h4>
                <dl className="vendor-detail__grid">
                  <div className="vendor-detail__item is-span-4">
                    <dt>주소</dt>
                    <dd
                      className={
                        detail.address || detail.addressDetail || detail.zipCode
                          ? undefined
                          : 'is-muted'
                      }
                    >
                      {formatPartVendorAddress(detail)}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="vendor-detail__section">
                <h4>결제 · 계좌</h4>
                <dl className="vendor-detail__grid">
                  <div className="vendor-detail__item is-span-2">
                    <dt>결제조건</dt>
                    <dd>{textOrDash(detail.paymentTerms)}</dd>
                  </div>
                  <div className="vendor-detail__item">
                    <dt>은행</dt>
                    <dd>{textOrDash(detail.bankName)}</dd>
                  </div>
                  <div className="vendor-detail__item">
                    <dt>예금주</dt>
                    <dd>{textOrDash(detail.accountHolder)}</dd>
                  </div>
                  <div className="vendor-detail__item is-span-2">
                    <dt>계좌번호</dt>
                    <dd>{textOrDash(detail.accountNumber)}</dd>
                  </div>
                </dl>
              </div>

              <div className="vendor-detail__section">
                <h4>메모</h4>
                <p className={detail.memo ? undefined : 'vendor-hint-inline'}>
                  {textOrDash(detail.memo)}
                </p>
              </div>
            </>
          ) : null}

          {detailTab === 'trade' ? (
            tradeLoading ? (
              <p className="vendor-hint-inline">거래내역 불러오는 중...</p>
            ) : tradeRows.length === 0 ? (
              <p className="vendor-hint-inline">
                이 업체로 등록된 인수검사가 없습니다. 인수검사 성적서의 공급업체명이 동일해야
                표시됩니다.
              </p>
            ) : (
              <table className="vendor-trade-table">
                <thead>
                  <tr>
                    <th>품명</th>
                    <th>Lot</th>
                    <th>입고수량</th>
                    <th>입고일</th>
                    <th>검사일</th>
                    <th>상태</th>
                    <th>판정</th>
                    <th>검사자</th>
                  </tr>
                </thead>
                <tbody>
                  {tradeRows.map((row) => (
                    <tr key={row.inspectionId}>
                      <td>
                        <div>{row.itemName || '-'}</div>
                        <div className="vendor-table__sub">{row.specText || ''}</div>
                      </td>
                      <td>{row.lotNo || '-'}</td>
                      <td>{row.incomingQty || '-'}</td>
                      <td>{row.incomingDate?.slice(0, 10) || '-'}</td>
                      <td>{row.inspectionDate?.slice(0, 10) || '-'}</td>
                      <td>{acceptanceStatusLabel(row.status)}</td>
                      <td>{row.overallJudgment || '-'}</td>
                      <td>{row.inspectorName || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : null}

          {detailTab === 'files' ? (
            <div>
              {renderAttachmentEditor(true)}
            </div>
          ) : null}

          {detailTab === 'assess' ? <VendorAssessmentSection vendorId={detail.vendorId} /> : null}
        </div>
      </div>
    );
  };

  const toolbarTitle =
    viewMode === 'create'
      ? '업체 등록'
      : viewMode === 'edit'
        ? '업체 수정'
        : viewMode === 'detail'
          ? '업체 상세'
          : '업체관리';

  const toolbarDesc =
    viewMode === 'create'
      ? '상단에서 바로 작성합니다. 저장 후 상세로 이동합니다.'
      : viewMode === 'edit'
        ? '업체 정보를 수정합니다.'
        : viewMode === 'detail'
          ? '더블클릭으로 진입한 상세 화면입니다. 거래내역·첨부·조사/평가를 확인하세요.'
          : '한 번 클릭은 선택, 더블클릭은 상세보기입니다.';

  return (
    <main className="contract-panel">
      <aside className="contract-panel__sidebar">
        <div className="contract-panel__side-header">
          <strong>업체관리</strong>
          <span>부품 납품처 마스터</span>
        </div>
        <div className="contract-panel__search">
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="업체명 · 사업자번호 · 담당자 검색"
          />
        </div>
        <div className="vendor-panel__filters">
          {(
            [
              { value: 'ALL', label: '전체' },
              { value: 'ACTIVE', label: '거래중' },
              { value: 'SUSPENDED', label: '중단' },
            ] as const
          ).map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={statusFilter === opt.value ? 'is-active' : undefined}
              onClick={() => setStatusFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="contract-panel__list-wrap">
          {loading ? (
            <p className="contract-panel__hint" style={{ padding: 12 }}>
              불러오는 중...
            </p>
          ) : filteredRows.length === 0 ? (
            <p className="contract-panel__hint" style={{ padding: 12 }}>
              등록된 업체가 없습니다.
            </p>
          ) : (
            <ul className="contract-panel__list">
              {filteredRows.map((row) => {
                const suspended = row.tradeStatus === 'SUSPENDED';
                return (
                  <li key={row.vendorId}>
                    <button
                      type="button"
                      className={`vendor-panel__list-item${
                        selectedId === row.vendorId ? ' is-active' : ''
                      }`}
                      onClick={() => selectRow(row)}
                      onDoubleClick={() => void openDetail(row)}
                    >
                      <div className="vendor-panel__list-top">
                        <span className="vendor-panel__list-name">{row.vendorName}</span>
                        <span
                          className={`vendor-panel__badge ${
                            suspended ? 'is-suspended' : 'is-active'
                          }`}
                        >
                          {partVendorTradeStatusLabel(row.tradeStatus)}
                        </span>
                      </div>
                      <span className="vendor-panel__list-meta">
                        {[row.contactName, row.mobile || row.phone].filter(Boolean).join(' · ') ||
                          '담당자 미등록'}
                        {(row.attachmentCount ?? 0) > 0 ? ` · 첨부 ${row.attachmentCount}` : ''}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>

      <section className="contract-panel__main vendor-panel__main">
        <div className="contract-panel__toolbar">
          <div>
            <h2>{toolbarTitle}</h2>
            <p>{toolbarDesc}</p>
          </div>
          {viewMode === 'list' ? (
            <button type="button" className="contract-panel__primary" onClick={openCreate}>
              업체 등록
            </button>
          ) : (
            <button type="button" className="contract-panel__ghost" onClick={backToList}>
              목록으로
            </button>
          )}
        </div>

        {error ? <p className="contract-panel__error">{error}</p> : null}

        <div className="vendor-panel__scroll">
          {viewMode === 'create' || viewMode === 'edit' ? renderForm() : null}
          {viewMode === 'detail' ? renderDetail() : null}

          {viewMode === 'list' ? (
            <div className="vendor-table-wrap">
              {loading ? (
                <p className="contract-panel__hint">불러오는 중...</p>
              ) : filteredRows.length === 0 ? (
                <p className="contract-panel__hint">
                  {keyword || statusFilter !== 'ALL'
                    ? '조건에 맞는 업체가 없습니다.'
                    : '등록된 업체가 없습니다. 「업체 등록」으로 추가하세요.'}
                </p>
              ) : (
                <table className="vendor-table">
                  <thead>
                    <tr>
                      <th>업체</th>
                      <th style={{ width: 120 }}>대표 / 사업자</th>
                      <th style={{ width: 140 }}>담당자</th>
                      <th style={{ width: 130 }}>연락처</th>
                      <th>주소</th>
                      <th style={{ width: 72 }}>첨부</th>
                      <th style={{ width: 88 }}>상태</th>
                      <th style={{ width: 120 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => {
                      const suspended = row.tradeStatus === 'SUSPENDED';
                      return (
                        <tr
                          key={row.vendorId}
                          className={selectedId === row.vendorId ? 'is-selected' : undefined}
                          onClick={() => selectRow(row)}
                          onDoubleClick={() => void openDetail(row)}
                        >
                          <td>
                            <div className="vendor-table__name">{row.vendorName}</div>
                            <div className="vendor-table__sub">
                              {row.supplyItems?.trim() ||
                                row.businessItem?.trim() ||
                                '납품품목 미등록'}
                            </div>
                          </td>
                          <td>
                            <div>{row.ceoName || '-'}</div>
                            <div className="vendor-table__sub">{row.businessNumber || '-'}</div>
                          </td>
                          <td>
                            <div>{row.contactName || '-'}</div>
                            <div className="vendor-table__sub">{row.contactPosition || ''}</div>
                          </td>
                          <td>
                            <div>{row.mobile || row.phone || '-'}</div>
                            <div className="vendor-table__sub">{row.email || ''}</div>
                          </td>
                          <td className={row.address ? undefined : 'is-muted'}>
                            {formatPartVendorAddress(row)}
                          </td>
                          <td>{row.attachmentCount ?? 0}</td>
                          <td>
                            <span
                              className={`vendor-panel__badge ${
                                suspended ? 'is-suspended' : 'is-active'
                              }`}
                            >
                              {partVendorTradeStatusLabel(row.tradeStatus)}
                            </span>
                          </td>
                          <td>
                            <div
                              className="vendor-table__actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="is-ghost"
                                onClick={() => void openDetail(row)}
                              >
                                상세
                              </button>
                              <button
                                type="button"
                                className="is-ghost"
                                onClick={() => void openEdit(row)}
                              >
                                수정
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}

export default VendorPanel;
